'use client';
import type { NextPage } from 'next';
import { useState } from 'react';
import styles from './date-picker.module.scss';

interface DatePickerProps {
  className?: string;
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
}

const DatePicker: NextPage<DatePickerProps> = ({
  className = '',
  label,
  value = '',
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const initialDate = new Date();
  initialDate.setDate(initialDate.getDate() + 1);
  const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth());
  const [currentYear, setCurrentYear] = useState(initialDate.getFullYear());

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDate = (dateString: string) => {
    if (!dateString) return null;
    return new Date(dateString + 'T00:00:00');
  };

  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return 'Select the date';
    const date = parseDate(dateString);
    if (!date) return 'Select the date';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const isPastDate = (day: number, month: number, year: number) => {
    const date = new Date(year, month, day);
    date.setHours(0, 0, 0, 0);
    return date < tomorrow;
  };

  const isSelectedDate = (day: number, month: number, year: number) => {
    if (!value) return false;
    const selectedDate = parseDate(value);
    if (!selectedDate) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === month &&
      selectedDate.getFullYear() === year
    );
  };

  const handleDateSelect = (day: number) => {
    if (isPastDate(day, currentMonth, currentYear)) return;

    const selectedDate = new Date(currentYear, currentMonth, day);
    const formattedDate = formatDate(selectedDate);
    onChange?.(formattedDate);
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={styles.emptyDay}></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isPast = isPastDate(day, currentMonth, currentYear);
      const isSelected = isSelectedDate(day, currentMonth, currentYear);

      days.push(
        <div
          key={day}
          className={`${styles.calendarDay} ${isPast ? styles.pastDay : ''} ${isSelected ? styles.selectedDay : ''}`}
          onClick={() => handleDateSelect(day)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  return (
    <div className={[styles.root, className].join(' ')}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.selectContainer}>
        <div className={styles.selectField} onClick={() => setIsOpen(!isOpen)}>
          <svg
            className={`${styles.calendarIcon} ${isOpen ? styles.calendarIconOpen : ''}`}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M12.6667 2.66663H3.33333C2.59695 2.66663 2 3.26358 2 3.99996V12.6666C2 13.403 2.59695 14 3.33333 14H12.6667C13.403 14 14 13.403 14 12.6666V3.99996C14 3.26358 13.403 2.66663 12.6667 2.66663Z"
              stroke="currentColor"
              strokeWidth="1.33333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.6667 1.33337V4.00004"
              stroke="currentColor"
              strokeWidth="1.33333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M5.33333 1.33337V4.00004"
              stroke="currentColor"
              strokeWidth="1.33333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2 6.66663H14"
              stroke="currentColor"
              strokeWidth="1.33333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span
            className={`${styles.selectText} ${!value ? styles.placeholder : ''}`}
          >
            {formatDisplayDate(value)}
          </span>
          <svg
            className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        {isOpen && (
          <div className={styles.calendar}>
            <div className={styles.calendarHeader}>
              <button
                type="button"
                className={styles.navButton}
                onClick={handlePrevMonth}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M7.5 9L4.5 6L7.5 3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span className={styles.monthYear}>
                {months[currentMonth]} {currentYear}
              </span>
              <button
                type="button"
                className={styles.navButton}
                onClick={handleNextMonth}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M4.5 3L7.5 6L4.5 9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div className={styles.calendarGrid}>
              <div className={styles.weekDays}>
                {daysOfWeek.map((day) => (
                  <div key={day} className={styles.weekDay}>
                    {day}
                  </div>
                ))}
              </div>
              <div className={styles.calendarDays}>{renderCalendarDays()}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatePicker;
